import { useMutation } from "@apollo/client/react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { match } from "ts-pattern";

import {
  GetTenantDocument,
  UpdateTenantOwnerDocument,
  type UpdateTenantOwnerMutation,
  type UpdateTenantOwnerMutationVariables,
} from "~/features/admin/tenants/gqls";
import { translateError } from "~/lib/errors";

interface UseTenantChangeOwnerOptions {
  onSuccess?: () => void;
}

/** Transfer the tenant ownership to another user. */
export function useTenantChangeOwner({ onSuccess }: UseTenantChangeOwnerOptions = {}) {
  const { t } = useTranslation();
  const [processing, setProcessing] = useState(false);
  const [updateOwner] = useMutation<UpdateTenantOwnerMutation, UpdateTenantOwnerMutationVariables>(
    UpdateTenantOwnerDocument,
    { refetchQueries: [GetTenantDocument], awaitRefetchQueries: true },
  );

  const submit = useCallback(
    async (tenantId: string, ownerUserId: string) => {
      setProcessing(true);
      try {
        const res = await updateOwner({ variables: { input: { id: tenantId, ownerUserId } } });
        match(res.data?.cnutUpdateTenantOwner)
          .with({ __typename: "ValidationError" }, (e) => toast.error(e.fields.map((f) => f.message).join(", ")))
          .with({ __typename: "TenantUpdateOwnerOutput" }, () => {
            toast.success(t("model.tenant.ownerUpdatedSuccessfully"));
            onSuccess?.();
          })
          .otherwise(() => {});
      } catch (e) {
        toast.error(translateError(t, e));
      } finally {
        setProcessing(false);
      }
    },
    [updateOwner, t, onSuccess],
  );

  return { submit, processing };
}
